/**
 * TransmissionPanel
 *
 * Self-contained panel for configuring correspondence transmission.
 * Uses unified TransmissionOptionsList for channel/contact selection.
 * Fetches its own data via hooks - only needs packageId, matterId, and onSend callback.
 *
 * Manual entry forms use local state with explicit Save to avoid lag on every keystroke.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type {
  CorrespondenceChannel,
  Transmission,
} from '@/services/api/correspondenceService';

import { usePackageTransmission } from '../hooks/usePackageTransmission';
import { usePackageValidation } from '../hooks/usePackageValidation';
import {
  ReadySummary,
  TransmissionOptionsList,
  TransmittedContent,
  TransmittedMailContent,
} from './components';
import { EmailTransmissionForm } from './EmailTransmissionForm';
import { FaxTransmissionForm } from './FaxTransmissionForm';
import { FormField } from './FormField';
import { MailTransmissionForm } from './MailTransmissionForm';
import { inputStyles, textareaStyles } from './styles';

// ============================================================================
// Types
// ============================================================================

/** Simplified props - component fetches its own data via hooks */
export interface TransmissionPanelProps {
  packageId: string;
  onSend: () => void | Promise<void>;
  /** Whether components have stale inputs and need re-rendering */
  hasStaleComponents?: boolean;
  /** Whether a render is currently in progress */
  isRendering?: boolean;
  /** Callback to trigger re-render */
  onRender?: () => void;
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export const TransmissionPanel: React.FC<TransmissionPanelProps> = ({
  packageId,
  onSend,
  hasStaleComponents = false,
  isRendering = false,
  onRender,
  className,
}) => {
  // ========== Hooks ==========
  const {
    transmission,
    channel,
    isValid,
    destinationDisplay,
    entityContacts,
    entityName,
    recipientId,
    recipientEntityType,
    selectedOption,
    selectTransmissionOption,
    updateTransmission,
    transmittedAt,
    transmittedBy,
    postgridLetterId,
    isLoading,
    isSaving,
    matterCmsEmail,
  } = usePackageTransmission({ packageId, skip: !packageId });

  const { validationStatus } = usePackageValidation({
    packageId,
    skip: !packageId,
  });

  // ========== Local Form State (for manual entry - avoids lag) ==========
  const [localTransmission, setLocalTransmission] =
    useState<Partial<Transmission> | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // Sync local state when server transmission changes (initial load or option selection)
  useEffect(() => {
    if (transmission && !isDirty) {
      setLocalTransmission(transmission);
    }
  }, [transmission, isDirty]);

  // Reset dirty state when option changes
  useEffect(() => {
    setIsDirty(false);
  }, [selectedOption?.type, channel]);

  // Get the current form value (local if dirty, otherwise server)
  const formValue = isDirty ? localTransmission : transmission;

  // Handle local form changes (no API call)
  const handleLocalChange = useCallback((partial: Partial<Transmission>) => {
    setLocalTransmission(partial);
    setIsDirty(true);
  }, []);

  // Save local changes to server
  const handleSaveChanges = useCallback(() => {
    if (!localTransmission) return;
    updateTransmission(localTransmission);
    setIsDirty(false);
  }, [localTransmission, updateTransmission]);

  // Discard local changes
  const handleDiscardChanges = useCallback(() => {
    setLocalTransmission(transmission);
    setIsDirty(false);
  }, [transmission]);

  // ========== Derived State ==========
  const isTransmitted = !!transmittedAt;
  const isManualEntry = selectedOption?.type === 'manual';
  const canSend = isValid && !isTransmitted && !isDirty;

  // Check if the matter CMS email is currently in the CC list (only for email channel)
  const isCmsEmailInCc = useMemo(() => {
    if (!matterCmsEmail || formValue?.type !== 'email' || !formValue.cc) {
      return false;
    }
    return formValue.cc.some(
      (email: string) => email.toLowerCase() === matterCmsEmail.toLowerCase(),
    );
  }, [matterCmsEmail, formValue]);

  // Handler to toggle CMS email in CC list
  const handleToggleCmsEmailCc = useCallback(
    (checked: boolean) => {
      if (!matterCmsEmail || !formValue || formValue.type !== 'email') return;

      const currentCc = formValue.cc ?? [];
      let newCc: string[];

      if (checked) {
        // Add CMS email if not already present
        if (
          !currentCc.some(
            (e: string) => e.toLowerCase() === matterCmsEmail.toLowerCase(),
          )
        ) {
          newCc = [...currentCc, matterCmsEmail];
        } else {
          newCc = currentCc;
        }
      } else {
        // Remove CMS email
        newCc = currentCc.filter(
          (e: string) => e.toLowerCase() !== matterCmsEmail.toLowerCase(),
        );
      }

      handleLocalChange({
        ...formValue,
        cc: newCc.length > 0 ? newCc : undefined,
      });
    },
    [matterCmsEmail, formValue, handleLocalChange],
  );

  const handleSend = async () => {
    if (!canSend) return;
    await onSend();
  };

  // ========== Transmitted State ==========
  if (transmittedAt && channel && destinationDisplay) {
    // For mailed letters with a PostGrid letter ID, show detailed mail tracking
    const isMailChannel =
      channel === 'mailed_letter' || channel === 'mailed_cheque';

    return (
      <div className={cn('flex flex-col h-full', className)}>
        <div className="flex-1 overflow-y-auto">
          {isMailChannel && postgridLetterId ? (
            <TransmittedMailContent
              postgridLetterId={postgridLetterId}
              recipientName={transmission?.display_name || ''}
              destinationDisplay={destinationDisplay}
              transmittedAt={transmittedAt}
              transmittedBy={transmittedBy}
            />
          ) : (
            <TransmittedContent
              channel={channel as CorrespondenceChannel}
              recipientName={transmission?.display_name || ''}
              destinationDisplay={destinationDisplay}
              transmittedAt={transmittedAt}
              transmittedBy={transmittedBy}
            />
          )}
        </div>
      </div>
    );
  }

  // Default state: show transmission form
  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto">
        {/* Unified transmission option selection */}
        <TransmissionOptionsList
          contacts={entityContacts}
          selectedOption={selectedOption}
          onSelectOption={selectTransmissionOption}
          isDisabled={isTransmitted}
          isLoading={isLoading}
          recipientId={recipientId}
          recipientEntityType={recipientEntityType}
          recipientName={entityName}
        />

        {/* Manual entry forms */}
        {isManualEntry && channel && (
          <div className="mt-4 space-y-3">
            {channel === 'fax' && formValue?.type === 'fax' && (
              <FaxTransmissionForm
                value={formValue}
                onChange={handleLocalChange}
                disabled={isTransmitted}
              />
            )}
            {channel === 'email' && formValue?.type === 'email' && (
              <EmailTransmissionForm
                value={formValue}
                onChange={handleLocalChange}
                disabled={isTransmitted}
                matterCmsEmail={matterCmsEmail}
              />
            )}
            {(channel === 'mailed_letter' || channel === 'mailed_cheque') &&
              formValue?.type === 'mailed_letter' && (
                <MailTransmissionForm
                  value={formValue}
                  onChange={handleLocalChange}
                  disabled={isTransmitted}
                />
              )}

            {/* Save/Discard buttons when form is dirty */}
            {isDirty && (
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleSaveChanges}
                  disabled={isSaving}
                  size="sm"
                  className="flex-1"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  onClick={handleDiscardChanges}
                  variant="outline"
                  size="sm"
                  disabled={isSaving}
                >
                  Discard
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Email extras for contact selection (CC, subject, body) */}
        {selectedOption?.type === 'contact' &&
          channel === 'email' &&
          formValue?.type === 'email' && (
            <div className="mt-4 space-y-3.5">
              <FormField
                label="CC"
                htmlFor="email-cc-contact"
                helperText="Separate multiple email addresses with commas"
              >
                <Input
                  id="email-cc-contact"
                  type="text"
                  placeholder="email1@example.com, email2@example.com"
                  value={formValue.cc?.join(', ') || ''}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    const emails = inputValue
                      .split(',')
                      .map((email) => email.trim())
                      .filter((email) => email.length > 0);
                    handleLocalChange({
                      ...formValue,
                      cc: emails.length > 0 ? emails : undefined,
                    });
                  }}
                  disabled={isTransmitted}
                  className={inputStyles}
                />
              </FormField>

              {/* CC Firm CMS Email checkbox */}
              {matterCmsEmail && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="cc-cms-email-contact"
                    checked={isCmsEmailInCc}
                    onCheckedChange={handleToggleCmsEmailCc}
                    disabled={isTransmitted}
                  />
                  <Label
                    htmlFor="cc-cms-email-contact"
                    className="text-sm text-muted-foreground cursor-pointer"
                  >
                    CC firm CMS email ({matterCmsEmail})
                  </Label>
                </div>
              )}

              <FormField
                label="Subject"
                htmlFor="email-subject-contact"
                characterCount={{
                  current: formValue.subject?.length || 0,
                  max: 100,
                  warningThreshold: 0.8,
                }}
                inlineCount
              >
                <Input
                  id="email-subject-contact"
                  type="text"
                  placeholder="Lien Notice - [Matter Name]"
                  value={formValue.subject || ''}
                  onChange={(e) =>
                    handleLocalChange({
                      ...formValue,
                      subject: e.target.value || undefined,
                    })
                  }
                  disabled={isTransmitted}
                  maxLength={100}
                  className={inputStyles}
                />
              </FormField>

              <FormField
                label="Message"
                htmlFor="email-body-contact"
                characterCount={{
                  current: formValue.body?.length || 0,
                  max: 5000,
                  warningThreshold: 0.8,
                }}
                inlineCount
              >
                <Textarea
                  id="email-body-contact"
                  placeholder="Add a message to include in the email body..."
                  value={formValue.body || ''}
                  onChange={(e) =>
                    handleLocalChange({
                      ...formValue,
                      body: e.target.value || undefined,
                    })
                  }
                  disabled={isTransmitted}
                  maxLength={5000}
                  className={textareaStyles}
                  rows={3}
                />
              </FormField>

              {/* Save/Discard buttons when form is dirty */}
              {isDirty && (
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={handleSaveChanges}
                    disabled={isSaving}
                    size="sm"
                    className="flex-1"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    onClick={handleDiscardChanges}
                    variant="outline"
                    size="sm"
                    disabled={isSaving}
                  >
                    Discard
                  </Button>
                </div>
              )}
            </div>
          )}
      </div>

      {/* Fixed Footer - Unified status + send action */}
      {channel && (
        <div className="flex-shrink-0 pt-4 border-t border-zinc-100">
          <ReadySummary
            channel={channel}
            recipientName={transmission?.display_name || ''}
            destinationDisplay={destinationDisplay}
            validationStatus={validationStatus}
            transmission={transmission}
            hasStaleComponents={hasStaleComponents}
            isRendering={isRendering}
            onRender={onRender}
            canSend={canSend}
            isSending={isSaving}
            onSend={handleSend}
          />
        </div>
      )}
    </div>
  );
};
